
import { useState } from 'react'
import { btn, cx, input, inputSm, label, notice } from '@/lib/ui'
import { errorMessage } from '@/lib/client-api'

/**
 * Supplies the figure the catalogue does not have.
 *
 * The person in front of the rack has the manual, and often a meter, and until
 * this existed had nowhere to put either: no API took a wattage and the device
 * editor had no field for one, so every figure in the catalogue arrived as a
 * CSV imported from a laptop. That is why 85 of the 219 devices that can draw
 * from a wall still have none.
 *
 * It writes for this workspace only, so it changes their own plans at once and
 * nobody else's, and asks for the provenance rather than guessing it — rule 1
 * survives the shortcut. MEASURED is offered first because it is the honest
 * answer for the gear that will never have a published figure: RODE and Zoom
 * publish adapter ratings, not consumption, and no amount of searching changes
 * that. A meter does.
 */
export function PowerFigureForm({
  deviceId,
  deviceLabel,
  apiBase,
  onSaved,
  onCancel,
}: {
  deviceId: string
  deviceLabel: string
  apiBase: string
  onSaved: () => void
  onCancel: () => void
}) {
  const [maxW, setMaxW] = useState('')
  const [typicalW, setTypicalW] = useState('')
  const [provenance, setProvenance] = useState<'MEASURED' | 'PUBLISHED'>('MEASURED')
  const [sourceUrl, setSourceUrl] = useState('')
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  async function save(event: React.FormEvent) {
    event.preventDefault()
    setError(null)
    setSaving(true)
    try {
      const response = await fetch(`${apiBase}/devices/${deviceId}/power`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          powerDrawMaxW: Number(maxW),
          powerDrawTypicalW: typicalW.trim() === '' ? null : Number(typicalW),
          provenance,
          sourceUrl,
          note,
        }),
      })
      const payload = await response.json()
      if (!response.ok) {
        const issues: string[] = Array.isArray(payload.details)
          ? payload.details.map((issue: { message?: string }) => issue.message).filter(Boolean)
          : []
        throw new Error(issues.length > 0 ? issues.join(' ') : (payload.error ?? 'Could not save the figure'))
      }
      onSaved()
    } catch (cause) {
      setError(errorMessage(cause, 'Could not save the figure'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <form onSubmit={save} className="mt-2 rounded-[var(--radius-card)] border border-[var(--border)] p-3">
      <p className="text-[11px] text-muted">
        A figure for <strong className="font-medium text-[var(--fg)]">{deviceLabel}</strong>, used by this
        workspace only until someone promotes it to the shared catalogue.
      </p>

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <div>
          <label className={label} htmlFor={`max-${deviceId}`}>
            Maximum watts
          </label>
          <input
            id={`max-${deviceId}`}
            className={cx(input, 'mt-1')}
            type="number"
            min="0.1"
            step="0.1"
            required
            value={maxW}
            onChange={(event) => setMaxW(event.target.value)}
          />
        </div>
        <div>
          <label className={label} htmlFor={`typ-${deviceId}`}>
            Typical watts <span className="normal-case tracking-normal">(optional)</span>
          </label>
          <input
            id={`typ-${deviceId}`}
            className={cx(input, 'mt-1')}
            type="number"
            min="0.1"
            step="0.1"
            value={typicalW}
            onChange={(event) => setTypicalW(event.target.value)}
          />
        </div>
      </div>

      <fieldset className="mt-3">
        <legend className={label}>Where it came from</legend>
        <div className="mt-1.5 flex flex-wrap gap-3 text-xs">
          {(
            [
              ['MEASURED', 'I measured it'],
              ['PUBLISHED', 'The maker publishes it'],
            ] as const
          ).map(([value, text]) => (
            <label key={value} className="flex items-center gap-1.5">
              <input
                type="radio"
                name={`prov-${deviceId}`}
                checked={provenance === value}
                onChange={() => setProvenance(value)}
              />
              {text}
            </label>
          ))}
        </div>
      </fieldset>

      {provenance === 'PUBLISHED' && (
        <div className="mt-3">
          <label className={label} htmlFor={`url-${deviceId}`}>
            Page it came from
          </label>
          <input
            id={`url-${deviceId}`}
            className={cx(input, 'mt-1')}
            type="url"
            placeholder="https://…"
            required
            value={sourceUrl}
            onChange={(event) => setSourceUrl(event.target.value)}
          />
        </div>
      )}

      <div className="mt-3">
        <label className={label} htmlFor={`note-${deviceId}`}>
          Note{' '}
          <span className="normal-case tracking-normal">
            {provenance === 'MEASURED' ? '(how you measured it)' : '(what the page says)'}
          </span>
        </label>
        <input
          id={`note-${deviceId}`}
          className={cx(inputSm, 'mt-1 w-full')}
          placeholder={
            provenance === 'MEASURED'
              ? 'Clamp meter at the distro, four-camera show'
              : 'Power consumption 200 W'
          }
          value={note}
          onChange={(event) => setNote(event.target.value)}
        />
      </div>

      {error && <p className={cx(notice.error, 'mt-3')}>{error}</p>}

      <div className="mt-3 flex gap-2">
        <button type="submit" className={cx(btn.sm, btn.primary)} disabled={saving}>
          {saving ? 'Saving…' : 'Save figure'}
        </button>
        <button type="button" className={cx(btn.sm, btn.secondary)} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  )
}
